import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { fmtMoney, fmtDate, EmptyState } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PlugsConnected, CheckCircle, Clock } from "@phosphor-icons/react";

export default function ReconnectionQueue() {
  const { lang } = useI18n();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { status: "pending_reconnection" } });
      setClients(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reconnect = async (c) => {
    if (!confirm(lang === "en"
      ? `Mark "${c.name}" as physically reconnected and move to Active?`
      : `¿Marcar a "${c.name}" como reconectado físicamente y mover a Activo?`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/clients/${c.id}/reconnect`);
      toast.success(lang === "en" ? "Client reconnected" : "Cliente reconectado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageShell
      section={lang === "en" ? "Clients" : "Clientes"}
      page={lang === "en" ? "Pending reconnection" : "Pendiente de reconexión"}
      context={`${clients.length} ${lang === "en" ? "in queue" : "en cola"}`}
      testid="reconnection-toolbar"
    >
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-4 mb-6 flex items-start gap-3" data-testid="reconnection-explainer">
        <Clock size={20} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 dark:text-amber-200">
          <strong>{lang === "en" ? "How this works" : "Cómo funciona"}:</strong>{" "}
          {lang === "en"
            ? "These clients were disconnected for non-payment and have now paid their balance in full. They are waiting for Support / Service to physically reconnect their meter. Once reconnected, mark them as Active here."
            : "Estos clientes fueron desconectados por falta de pago y ya pagaron toda su deuda. Esperan que Support / Service los reconecte físicamente. Una vez reconectados, márcalos como Activo aquí."}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40">
              <tr className="text-left">
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Client" : "Cliente"}</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Address" : "Dirección"}</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Disconnected" : "Desconectado"}</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Paid on" : "Pagó el"}</th>
                <th className="px-5 py-3 text-right text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Monthly" : "Mensual"}</th>
                <th className="px-5 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading
                ? <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">…</td></tr>
                : clients.length === 0
                  ? <tr><td colSpan={6} className="px-5 py-12">
                      <EmptyState
                        icon={CheckCircle}
                        title={lang === "en" ? "No clients pending reconnection" : "Sin clientes pendientes de reconexión"}
                        message={lang === "en" ? "All paid-up clients are already active." : "Todos los clientes al día están activos."}
                      />
                    </td></tr>
                  : clients.map((c) => (
                    <tr key={c.id} data-testid={`reconnection-row-${c.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900 dark:text-slate-100">{c.name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{c.email || "—"}</div>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-300">{c.service_address || c.address || "—"}</td>
                      <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">{c.disconnected_at ? fmtDate(c.disconnected_at) : "—"}</td>
                      <td className="px-5 py-3 text-xs text-emerald-700 dark:text-emerald-400 font-medium whitespace-nowrap">{c.paid_pending_reconnection_at ? fmtDate(c.paid_pending_reconnection_at) : "—"}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-700 dark:text-slate-300">{fmtMoney(c.monthly_fee)}</td>
                      <td className="px-5 py-3 text-right">
                        <Button
                          onClick={() => reconnect(c)}
                          disabled={busyId === c.id}
                          data-testid={`reconnect-btn-${c.id}`}
                          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        >
                          <PlugsConnected size={13} className="mr-1" />
                          {lang === "en" ? "Reconnected" : "Reconectado"}
                        </Button>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}
